import { Modal, RadioButton, RadioButtonGroup, TextArea } from '@carbon/react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { UpdateEvent } from 'api/dtos/event';
import sharedMessages from 'Components/utils/sharedMessages';
import styles from './DeclineEventModal.module.scss';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string, mode?: UpdateEvent) => void;
    isRecurring?: boolean;
    isLoading?: boolean;
};

const DeclineEventModal = ({ isOpen, onClose, onConfirm, isRecurring, isLoading }: Props) => {
    const intl = useIntl();
    const [reason, setReason] = useState('');
    const [mode, setMode] = useState<UpdateEvent>(UpdateEvent.Single);

    const handleSubmit = () => {
        onConfirm(reason, isRecurring ? mode : undefined);
        setReason('');
        setMode(UpdateEvent.Single);
    };

    const handleClose = () => {
        setReason('');
        setMode(UpdateEvent.Single);
        onClose();
    };

    return (
        <Modal
            open={isOpen}
            onRequestClose={handleClose}
            onRequestSubmit={handleSubmit}
            modalHeading={intl.formatMessage(messages.heading)}
            primaryButtonText={intl.formatMessage(messages.confirm)}
            secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            danger
            preventCloseOnClickOutside
            primaryButtonDisabled={isLoading}
            selectorPrimaryFocus="#decline-reason"
        >
            <p>{intl.formatMessage(messages.description)}</p>
            {isOpen && (
                <TextArea
                    id="decline-reason"
                    labelText={intl.formatMessage(messages.reasonLabel)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder={intl.formatMessage(messages.reasonPlaceholder)}
                    value={reason}
                    onChange={(e) => { setReason(e.target.value); }}
                    rows={3}
                    className={styles.inputContainer}
                />
            )}
            {isRecurring && (
                <RadioButtonGroup
                    legendText={intl.formatMessage(messages.scopeLabel)}
                    name="decline-event-scope"
                    valueSelected={String(mode)}
                    onChange={(value) => { setMode(Number(value) as UpdateEvent); }}
                >
                    <RadioButton
                        id="decline-event-single"
                        value={String(UpdateEvent.Single)}
                        labelText={intl.formatMessage(messages.singleScope)}
                    />
                    <RadioButton
                        id="decline-event-future"
                        value={String(UpdateEvent.Future)}
                        labelText={intl.formatMessage(messages.futureScope)}
                    />
                </RadioButtonGroup>
            )}
        </Modal>
    );
};

const messages = defineMessages({
    heading: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.heading',
        defaultMessage: 'Wypisz się z wydarzenia',
    },
    description: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.description',
        defaultMessage: 'Czy na pewno chcesz wypisać się z tego wydarzenia?',
    },
    reasonLabel: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.reasonLabel',
        defaultMessage: 'Powód (opcjonalnie)',
    },
    reasonPlaceholder: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.reasonPlaceholder',
        defaultMessage: 'Podaj powód wypisania się...',
    },
    scopeLabel: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.scopeLabel',
        defaultMessage: 'Zakres rezygnacji',
    },
    singleScope: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.singleScope',
        defaultMessage: 'Tylko to wydarzenie',
    },
    futureScope: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.futureScope',
        defaultMessage: 'To i przyszłe',
    },
    confirm: {
        id: 'src.Screens.Dashboard.Events.DeclineEventModal.confirm',
        defaultMessage: 'Wypisz się',
    },
});

export default DeclineEventModal;
